// packages/whiteboard/src/features/textbox/textbox-options.config.ts

import {
  FILL_ACTIONS,
  BORDER_ACTIONS,
  BORDER_WIDTH_ACTIONS,
  BORDER_RADIUS_ACTIONS,
  BORDER_STYLE_ACTIONS,
  BOX_SHADOW_ACTIONS,
  BACKGROUND_OPACITY_ACTIONS,
  Z_ORDER_ACTIONS,
  ALIGN_ACTIONS,
  BOARD_COMMAND_ACTIONS,
  TEXT_ACTIONS,
  TEXT_SIZE_ACTIONS,
  TEXT_WEIGHT_ACTIONS,
  FONT_STYLE_ACTIONS,
  TEXT_DECORATION_ACTIONS,
  type ActionIntent,
  type ActionsMap,
  type OptionsCircleDef,
} from '../../optionsbar/optionsbar-actions.js';
import { composeOptionsCircle } from '../../optionsbar/parts/compose-options-circle.js';

// Optionskreis aus Einzelteilen (Textblock = 1+2+3+4+5+10): Standard-Knöpfe +
// Rahmen/Linien-Optionen + Füllfarben + Text-Ausrichtung/Listen + Eckenrundung
// + Schriftgrößen/Textfarben/Textoptionen.
const textSvg = composeOptionsCircle([1, 2, 3, 4, 5, 10]);

/** Horizontal text alignment. */
const TEXT_ALIGN_ACTIONS: ActionsMap = {
  'action-text-align-left':   { intent: 'text-align', value: 'left' },
  'action-text-align-center': { intent: 'text-align', value: 'center' },
  'action-text-align-right':  { intent: 'text-align', value: 'right' },
};

/** List type — numered=ordered, unnumered=bullet, checkboxes=checklist. */
const LIST_TYPE_ACTIONS: ActionsMap = {
  'action-list-numered':    { intent: 'list-type', value: 'ordered' },
  'action-list-unnumered':  { intent: 'list-type', value: 'bullet' },
  'action-list-checkboxes': { intent: 'list-type', value: 'checklist' },
};

/** Text transform — uppercase / lowercase. */
const TEXT_TRANSFORM_ACTIONS: ActionsMap = {
  'action-text-transform-uppercase': { intent: 'text-transform', value: 'uppercase' },
  'action-text-transform-lowercase': { intent: 'text-transform', value: 'lowercase' },
};

/** Vertical script — superscript / subscript. */
const TEXT_VERTICAL_ALIGN_ACTIONS: ActionsMap = {
  'action-text-sup': { intent: 'text-vertical-align', value: 'super' },
  'action-text-sub': { intent: 'text-vertical-align', value: 'sub' },
};

/** Vertical text alignment. */
const V_ALIGN_ACTIONS: ActionsMap = {
  'action-text-valign-top':    { intent: 'v-align', value: 'top' },
  'action-text-valign-center': { intent: 'v-align', value: 'middle' },
  'action-text-valign-bottom': { intent: 'v-align', value: 'bottom' },
};

/**
 * Resolves an optionsbar action intent to a TextBox property name (PRD-F6 §TR12).
 * Returning `null` means the intent does not apply to this slice.
 */
function resolveTextboxProperty(intent: ActionIntent): string | null {
  switch (intent) {
    case 'fill':                 return 'background';
    case 'border-color':         return 'borderColor';
    case 'border-width':         return 'borderWidth';
    case 'border-style':         return 'borderStyle';
    case 'text-color':           return 'color';
    case 'text-size':            return 'fontSize';
    case 'text-weight':          return 'fontWeight';
    case 'text-align':           return 'hAlign';
    case 'list-type':            return 'listType';
    case 'text-decoration':      return 'textDecoration';
    case 'font-style':           return 'fontStyle';
    case 'text-transform':       return 'textTransform';
    case 'text-vertical-align':  return 'verticalScript';
    case 'box-shadow':           return 'boxShadow';
    case 'background-opacity':   return 'backgroundOpacity';
    case 'border-radius':        return 'cornerRadius';
    case 'v-align':              return 'vAlign';
    case 'z-order':
    case 'board-command':
      // No property update — handled as commands by the optionsbar element.
      return null;
    default:
      return null;
  }
}

/**
 * Textbox-specific optionsbar definition (PRD-F6 §TR12). The slice owns the
 * SVG/action mapping and the intent → property resolution for text boxes.
 */
export const TEXTBOX_OPTIONS: OptionsCircleDef = {
  svg: textSvg,
  actions: {
    ...FILL_ACTIONS,
    ...TEXT_ACTIONS,
    ...BORDER_ACTIONS,
    ...BORDER_WIDTH_ACTIONS,
    ...BORDER_STYLE_ACTIONS,
    ...TEXT_SIZE_ACTIONS,
    ...TEXT_WEIGHT_ACTIONS,
    ...TEXT_ALIGN_ACTIONS,
    ...LIST_TYPE_ACTIONS,
    ...TEXT_DECORATION_ACTIONS,
    ...FONT_STYLE_ACTIONS,
    ...TEXT_TRANSFORM_ACTIONS,
    ...TEXT_VERTICAL_ALIGN_ACTIONS,
    ...BOX_SHADOW_ACTIONS,
    ...BACKGROUND_OPACITY_ACTIONS,
    ...BORDER_RADIUS_ACTIONS,
    ...V_ALIGN_ACTIONS,
    ...Z_ORDER_ACTIONS,
    ...ALIGN_ACTIONS,
    ...BOARD_COMMAND_ACTIONS,
  },
  resolveProperty: resolveTextboxProperty,
};
